import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Layers, Plus, Sparkles, Send, LogOut, ExternalLink, Trash2, Handshake, LayoutTemplate, X, Plug,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, imgUrl } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = !!user?.features?.builder;
  const isStaff = !!user?.features?.staff;
  const isBeta = !!user?.features?.beta;
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showInteg, setShowInteg] = useState(false);

  const load = async () => {
    const [p, t] = await Promise.all([api.get("/projects"), api.get("/templates")]);
    setProjects(p.data.projects);
    setTemplates(t.data.templates);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const seed = localStorage.getItem("bf_seed_prompt");
    if (seed && isAdmin) {
      setPrompt(seed);
      localStorage.removeItem("bf_seed_prompt");
    }
  }, []); // eslint-disable-line

  const generate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/projects/generate", { prompt });
      toast.success("Site generated!");
      setPrompt("");
      nav(`/editor/${data.project.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const fromTemplate = async (templateId) => {
    setShowTemplates(false);
    try {
      const { data } = await api.post("/projects/from-template", { template_id: templateId });
      toast.success("Project created from template");
      nav(`/editor/${data.project.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create project");
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    setProjects((p) => p.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  return (
    <div className="min-h-screen bg-[#f2f0eb]">
      {/* top bar */}
      <header className="backdrop-blur-xl bg-[#f2f0eb]/80 border-b border-[#d3d3d3] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-black text-xl tracking-tight">
            <div className="w-7 h-7 bg-[#ff3b00] flex items-center justify-center text-white"><Layers size={16} /></div>
            BuildForged
          </div>
          <div className="flex items-center gap-4">
            {isStaff && (
              <Link to="/admin" data-testid="dashboard-admin-link" className="hidden sm:flex items-center gap-2 bg-[#0a2540] text-white px-3 py-1.5 hover:bg-[#ff3b00] transition-colors">
                <span className="label-tech" style={{ color: "#fff" }}>ADMIN</span>
              </Link>
            )}
            {isBeta && (
              <span data-testid="beta-badge" title="Beta tester — early access + unlimited usage" className="hidden sm:flex items-center gap-1.5 bg-[#0055ff] text-white px-3 py-1.5">
                <Sparkles size={13} /> <span className="label-tech" style={{ color: "#fff" }}>BETA</span>
              </span>
            )}
            {isAdmin && (
              <button onClick={() => setShowInteg(true)} data-testid="account-integrations-btn" className="hidden sm:flex items-center gap-2 border border-[#0a2540] px-3 py-1.5 hover:bg-[#0a2540] hover:text-white transition-colors">
                <Plug size={14} /> <span className="label-tech">INTEGRATIONS</span>
              </button>
            )}
            <Link to="/pricing" data-testid="dashboard-plan-badge" className="hidden sm:flex items-center gap-2 border border-[#0a2540] px-3 py-1.5 hover:bg-[#0a2540] hover:text-white transition-colors">
              <span className="label-tech">{(user?.plan || "free").toUpperCase()}</span>
              <span className="text-xs font-semibold text-[#ff3b00]">Upgrade</span>
            </Link>
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold">{user?.name}</div>
              <div className="label-tech">{isAdmin ? "AGENCY OWNER" : "CLIENT"}</div>
            </div>
            <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }} className="border border-[#0a2540] p-2.5 hover:bg-[#0a2540] hover:text-white transition-colors"><LogOut size={16} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {isAdmin && (
          <section className="mb-12">
            <div className="label-tech mb-3">/ NEW SITE</div>
            <div className="bg-white border-2 border-[#0055ff] shadow-[0_0_25px_rgba(0,85,255,0.1)] p-2 flex flex-col sm:flex-row items-stretch gap-2">
              <div className="flex items-center flex-1 gap-2">
                <Sparkles size={18} className="ml-3 text-[#0055ff] shrink-0" />
                <input
                  data-testid="dashboard-prompt-input"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && generate()}
                  placeholder="Describe the website you want to build…"
                  className="flex-1 bg-transparent font-tech text-sm py-3 outline-none placeholder:text-gray-400"
                />
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="generate-site-btn"
                  onClick={generate}
                  disabled={generating}
                  className="bg-[#ff3b00] text-white font-semibold px-5 py-3 flex items-center justify-center gap-2 hover:bg-[#e63500] disabled:opacity-60 transition-colors"
                >
                  {generating ? "Building…" : (<>Generate <Send size={16} /></>)}
                </button>
                <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
                  <DialogTrigger asChild>
                    <button data-testid="open-templates-btn" className="border border-[#0a2540] px-4 py-3 flex items-center gap-2 hover:bg-[#0a2540] hover:text-white transition-colors">
                      <LayoutTemplate size={16} /> <span className="hidden sm:inline text-sm font-semibold">Templates</span>
                    </button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#f2f0eb] border border-[#0a2540] max-w-2xl">
                    <DialogHeader><DialogTitle className="font-heading">Start from a template</DialogTitle></DialogHeader>
                    <div className="grid sm:grid-cols-2 gap-4 mt-2">
                      {templates.map((t) => (
                        <button key={t.id} data-testid={`template-${t.id}`} onClick={() => fromTemplate(t.id)} className="text-left bg-white border border-[#0a2540] shadow-hard p-4 hover:-translate-y-0.5 transition-transform">
                          <img src={imgUrl(t.thumbnailQuery, 400, 220)} alt={t.name} className="w-full h-28 object-cover mb-3" />
                          <div className="font-heading font-bold">{t.name}</div>
                          <div className="text-sm text-[#475569]">{t.description}</div>
                        </button>
                      ))}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            {generating && <div className="label-tech mt-3 text-[#ff3b00] animate-pulse">✦ Claude is designing your site — this can take ~20s…</div>}
          </section>
        )}

        <div className="flex items-center justify-between mb-6">
          <div className="label-tech">/ {isAdmin ? "YOUR PROJECTS" : "SITES SHARED WITH YOU"} ({projects.length})</div>
        </div>

        {loading ? (
          <div className="label-tech animate-pulse">Loading projects…</div>
        ) : projects.length === 0 ? (
          <div className="bg-white border border-dashed border-[#0a2540] p-16 text-center">
            <div className="font-heading font-bold text-xl mb-2">{isAdmin ? "No sites yet" : "Nothing here yet"}</div>
            <p className="text-[#475569]">{isAdmin ? "Describe a website above to generate your first one." : "Your agency hasn't shared a site with you yet."}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((p, i) => (
              <motion.div
                key={p.id}
                data-testid={`project-card-${p.id}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => nav(`/editor/${p.id}`)}
                className="group bg-white border border-[#0a2540] shadow-hard hover:-translate-y-1 transition-all cursor-pointer overflow-hidden"
              >
                <div className="h-40 overflow-hidden border-b border-[#0a2540] relative" style={{ background: p.schema_data?.meta?.primaryColor || "#0a2540" }}>
                  <img src={imgUrl(heroQuery(p), 500, 300)} alt={p.name} className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform" />
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-heading font-bold text-lg leading-tight">{p.name}</div>
                      <div className="label-tech mt-1">{p.schema_data?.sections?.length || 0} SECTIONS</div>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <a href={`/preview/${p.id}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`preview-${p.id}`} className="p-2 border border-[#d3d3d3] hover:border-[#0055ff] hover:text-[#0055ff]"><ExternalLink size={14} /></a>
                      {isAdmin && <button data-testid={`delete-${p.id}`} onClick={(e) => remove(p.id, e)} className="p-2 border border-[#d3d3d3] hover:border-[#ff3b00] hover:text-[#ff3b00]"><Trash2 size={14} /></button>}
                    </div>
                  </div>
                  {p.customer_email && (
                    <div className="mt-3 flex items-center gap-1.5 text-xs font-tech text-[#166534] bg-[#166534]/10 px-2 py-1 w-fit">
                      <Handshake size={12} /> {isAdmin ? `Handed off → ${p.customer_email}` : "Shared with you"}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
      {isAdmin && <AccountIntegrationsDialog open={showInteg} onOpenChange={setShowInteg} />}
    </div>
  );
}

const ACCOUNT_INTEG_FIELDS = [
  ["googleAnalytics", "Google Analytics (GA4 ID)", "G-XXXXXXX"],
  ["whatsapp", "WhatsApp number (with country code)", "15551234567"],
  ["whatsappMessage", "WhatsApp prefilled message", "Hi! I'm interested…"],
  ["calendly", "Calendly link", "https://calendly.com/you/30min"],
  ["mailchimp", "Mailchimp form action URL", "https://…list-manage.com/subscribe/post?u=…"],
  ["instagram", "Instagram URL", "https://instagram.com/you"],
  ["facebook", "Facebook URL", "https://facebook.com/you"],
  ["twitter", "X / Twitter URL", "https://x.com/you"],
  ["linkedin", "LinkedIn URL", "https://linkedin.com/company/you"],
  ["youtube", "YouTube URL", "https://youtube.com/@you"],
  ["googleMaps", "Business address or Maps embed URL", "350 5th Ave, New York"],
];

function AccountIntegrationsDialog({ open, onOpenChange }) {
  const [integ, setInteg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api.get("/account/integrations").then((r) => setInteg(r.data.integrations || {})).catch(() => setInteg({}));
  }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/account/integrations", { integrations: integ });
      toast.success("Account integrations saved — they'll pre-fill every new site you create.");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#f2f0eb] border border-[#0a2540] max-w-lg max-h-[85vh] overflow-auto">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Plug size={18} /> Account integrations</DialogTitle></DialogHeader>
        <p className="text-xs text-[#475569] -mt-1">Set defaults once — they auto-fill every NEW site you generate. You can override them per-site in the editor's Apps tab.</p>
        {!integ ? (
          <div className="label-tech animate-pulse py-6 text-center">Loading…</div>
        ) : (
          <div className="space-y-3 mt-2">
            {ACCOUNT_INTEG_FIELDS.map(([k, label, ph]) => (
              <div key={k}>
                <label className="label-tech block mb-1">{label}</label>
                <input
                  data-testid={`account-integ-${k}`}
                  value={integ[k] || ""}
                  onChange={(e) => setInteg({ ...integ, [k]: e.target.value })}
                  placeholder={ph}
                  className="w-full bg-white border border-[#d3d3d3] p-2.5 text-sm outline-none focus:border-[#ff3b00]"
                />
              </div>
            ))}
            <button data-testid="account-integ-save" onClick={save} disabled={busy} className="w-full bg-[#ff3b00] text-white font-semibold py-3 hover:bg-[#e63500] disabled:opacity-60 transition-colors">
              {busy ? "Saving…" : "Save defaults"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function heroQuery(p) {
  const hero = (p.schema_data?.sections || []).find((s) => s.type === "hero");
  return hero?.content?.imageQuery || p.schema_data?.meta?.title || "website";
}
